// Схлопывание подряд идущих цитат в «серии источника» (citation runs).
//
// Проблема: и чат репетитора, и транскрипт аудио-резюме вешают citation-чип на
// КАЖДЫЙ блок (абзац / пункт списка / реплику). Когда десять абзацев подряд
// ссылаются на один и тот же фрагмент, экран превращается в частокол
// одинаковых чипов.
//
// Модель: идём по блокам подряд и склеиваем их в серию, пока блок не вводит
// НОВЫЙ источник. Чип рисуется один раз — на ПОСЛЕДНЕМ цитирующем блоке серии
// (он закрывает серию перед сменой источника, а не открывает её), и несёт
// накопленный набор источников серии.
//
// Правила:
//  - блок без источников серию не разрывает (и якорем не становится);
//  - блок с подмножеством уже показанных источников серию продолжает,
//    двигая якорь на себя ([A] после [A,B] — не повод для нового чипа);
//  - блок с новым источником закрывает предыдущую серию и открывает свою.

export interface CitationRun {
	/** Индекс блока, на котором рисуется чип (последний цитирующий в серии). */
	anchorIndex: number;
	/** Накопленный набор источников серии, в порядке первого появления. */
	markers: string[];
	/** Индексы всех цитирующих блоков серии — для подсветки покрытия. */
	memberIndices: number[];
}

/**
 * Группирует последовательность блоков (по одному набору источников на блок)
 * в серии. Возвращает серии в порядке документа.
 */
export function groupCitationRuns(blockMarkers: readonly (readonly string[])[]): CitationRun[] {
	const runs: CitationRun[] = [];
	let current: CitationRun | null = null;
	let seen = new Set<string>();

	for (let i = 0; i < blockMarkers.length; i++) {
		const markers = dedupe(blockMarkers[i] ?? []);
		if (markers.length === 0) continue;

		const isContinuation = current !== null && markers.every((m) => seen.has(m));
		if (isContinuation && current) {
			// Подмножество уже показанного — та же серия, якорь съезжает вниз.
			current.anchorIndex = i;
			current.memberIndices.push(i);
			continue;
		}

		// Новый источник — предыдущая серия закрывается своим чипом.
		if (current) runs.push(current);
		current = { anchorIndex: i, markers, memberIndices: [i] };
		seen = new Set(markers);
	}
	if (current) runs.push(current);

	return runs;
}

/**
 * Плоская карта `индекс блока → серия`, чтобы рендер не искал серию линейным
 * поиском на каждый блок.
 */
export interface CitationRunIndex {
	/** Серия, чип которой рисуется на этом блоке (иначе — чипа нет). */
	anchorAt: Map<number, CitationRun>;
	/** Порядковый номер серии, к которой принадлежит цитирующий блок. */
	runOf: Map<number, number>;
}

export function indexCitationRuns(
	blockMarkers: readonly (readonly string[])[],
	options: { dropLastRun?: boolean } = {},
): CitationRunIndex {
	const runs = groupCitationRuns(blockMarkers);
	// Во время стриминга последняя серия ещё растёт — её чип прыгал бы вниз
	// с каждым новым абзацем. Показываем его после завершения ответа.
	const visible = options.dropLastRun ? runs.slice(0, -1) : runs;

	const anchorAt = new Map<number, CitationRun>();
	const runOf = new Map<number, number>();
	for (let r = 0; r < runs.length; r++) {
		const run = runs[r];
		if (!run) continue;
		for (const idx of run.memberIndices) runOf.set(idx, r);
	}
	for (const run of visible) anchorAt.set(run.anchorIndex, run);

	return { anchorAt, runOf };
}

function dedupe(markers: readonly string[]): string[] {
	const out: string[] = [];
	for (const m of markers) {
		if (m && !out.includes(m)) out.push(m);
	}
	return out;
}
